import { Check } from 'lucide-react';

/**
 * AgentTraceBlock — Claude Code-style vertical timeline of agent steps.
 *
 * Props:
 *   steps: [{label, status: 'done'|'in_progress', summary?}]
 *   isThinking: bool — show "Thinking…" spinner when no steps yet
 *
 * Colors (on dark background):
 *   done        → emerald circle with checkmark
 *   in_progress → white spinner (animated)
 *   thinking    → faint white spinner + italic label
 */
const AgentTraceBlock = ({ steps = [], isThinking = false }) => {
  const showThinkingOnly = isThinking && steps.length === 0;
  const allDone = steps.length > 0 && steps.every((s) => s.status === 'done');

  if (!showThinkingOnly && steps.length === 0) return null;

  if (showThinkingOnly) {
    return (
      <div className="my-2 flex items-center gap-3 pl-1">
        <ThinkingDot />
        <span className="text-sm text-white/50 italic">Thinking…</span>
      </div>
    );
  }

  return (
    <div className="my-2 pl-1">
      {steps.map((step, i) => {
        const isLast = i === steps.length - 1;
        const isDone = step.status === 'done';

        return (
          <div key={i} className="relative flex gap-3">
            {/* Absolute vertical line: runs from below dot to bottom of row */}
            {!isLast && (
              <div
                className="absolute bg-white/20"
                style={{ left: '6px', top: '16px', bottom: 0, width: '1.5px' }}
              />
            )}

            {/* Dot — z-10 so it sits above the line */}
            <div className="relative z-10 flex-shrink-0 mt-[3px]">
              {isDone ? <DoneDot /> : <ActiveDot />}
            </div>

            {/* Label + summary — pb-3 gives the line room to run through */}
            <div className={`min-w-0 flex items-baseline gap-2 ${isLast ? 'pb-1' : 'pb-3'}`}>
              <span
                className={`text-sm leading-5 ${
                  isDone ? 'text-white/50 line-through decoration-white/20' : 'text-white font-medium'
                }`}
              >
                {step.label}
              </span>
              {step.summary && (
                <span className="text-xs text-white/40 flex-shrink-0 leading-5">
                  {step.summary}
                </span>
              )}
            </div>
          </div>
        );
      })}

      {/* Completed pill — shown once every step is done */}
      {allDone && (
        <div className="mt-2 ml-0.5 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-0.5">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          <span className="text-xs text-emerald-400 font-medium">Completed</span>
        </div>
      )}
    </div>
  );
};

/* Emerald circle with checkmark for completed steps */
const DoneDot = () => (
  <div className="w-3.5 h-3.5 rounded-full bg-emerald-500/80 flex items-center justify-center ring-1 ring-emerald-400/30">
    <Check size={8} className="text-white" strokeWidth={3} />
  </div>
);

/* White spinner for the actively executing step */
const ActiveDot = () => (
  <div
    className="w-3.5 h-3.5 rounded-full border-2 border-white/60 animate-spin"
    style={{ borderTopColor: 'transparent' }}
  />
);

/* Faint white spinner for pure thinking (no steps yet) */
const ThinkingDot = () => (
  <div
    className="w-3.5 h-3.5 rounded-full border-2 border-white/40 animate-spin flex-shrink-0"
    style={{ borderTopColor: 'transparent' }}
  />
);

export default AgentTraceBlock;
